"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordHmTimingSchema = exports.HM_MAX_TIMED_MS = exports.HM_TIMED_ACTIONS = exports.HM_ACTION_KINDS = exports.HM_BALL_IN_COURT = exports.EMPTY_FUNNEL = exports.FUNNEL_BUCKET_LABELS = exports.FUNNEL_BUCKETS = void 0;
exports.usesHiringManagerSurface = usesHiringManagerSurface;
exports.funnelBucketOf = funnelBucketOf;
exports.compareHmActions = compareHmActions;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// S10 — the hiring manager surface: HM-01 My openings, HM-02 My actions, HM-03
// Request a hire. Master plan S10, E16, and the product-owner decisions of
// 2026-09-28 recorded in ADR 0012.
// --- Who gets the three-screen surface ------------------------------------------
/** Requisition scopes that only ever reach "mine, or waiting on me". */
const NARROW_REQUISITION_SCOPES = ['own_requests', 'assigned_only'];
/**
 * Whether this person's whole world is HM-01/02/03 (E16: "Their entire world is
 * THREE screens").
 *
 * Judged per role, from permissions rather than role names (F2.08 clones): at least
 * one role reaches requisitions as `own_requests`, and no role reaches any wider
 * than "my own requests, or ones waiting on me". A recruiter or HR Lead who also
 * manages a team keeps the full application — they do HR's work too.
 *
 * Per role, not merged: merging keeps whichever of two equal scoped cells came
 * first, which would make the answer depend on the order roles were granted.
 */
function usesHiringManagerSurface(reaches) {
    const granted = reaches.filter((reach) => reach !== undefined && reach.level !== 'none');
    if (!granted.some((reach) => reach.scope === 'own_requests'))
        return false;
    return granted.every((reach) => reach.level === 'scoped' &&
        NARROW_REQUISITION_SCOPES.some((scope) => scope === reach.scope));
}
// --- HM-01: the funnel mini-bar --------------------------------------------------
/** E16's "stage funnel mini-bar": five buckets a manager reads at a glance. */
exports.FUNNEL_BUCKETS = ['screening', 'assessment', 'interview', 'offer', 'joined'];
exports.FUNNEL_BUCKET_LABELS = {
    screening: 'Screening',
    assessment: 'Assessment',
    interview: 'Interview',
    offer: 'Offer',
    joined: 'Joined',
};
/**
 * Where a live application counts. Closed ones (rejected, withdrawn, moved to
 * another job, the job closing) are not in the funnel — it shows who is still in
 * play. `hold` is still in play and counts where review happens.
 */
const BUCKET_OF_STAGE = {
    new: 'screening',
    screening: 'screening',
    hold: 'screening',
    assessment: 'assessment',
    interview_1: 'interview',
    interview_2: 'interview',
    interview_3: 'interview',
    offer: 'offer',
    hired_pending_join: 'offer',
    joined: 'joined',
};
function funnelBucketOf(stage) {
    return BUCKET_OF_STAGE[stage] ?? null;
}
exports.EMPTY_FUNNEL = {
    screening: 0,
    assessment: 0,
    interview: 0,
    offer: 0,
    joined: 0,
};
// --- HM-01: whose move it is -------------------------------------------------------
/**
 * E16's "waiting on: you / HR / candidate", plus the approver — whose desk a
 * request sits on for most of its first week — and nobody, for a paused one.
 */
exports.HM_BALL_IN_COURT = ['you', 'approver', 'hr', 'candidate', 'nobody'];
// --- HM-02: everything waiting on the manager --------------------------------------
exports.HM_ACTION_KINDS = ['approval', 'finish_draft', 'sent_back', 'rejected', 'decision', 'offer_approval', 'shortlist'];
/**
 * Overdue first, then the longest waiting (master plan S10: "overdue first").
 * Shared so the screen keeps the server's order when it removes an item.
 */
function compareHmActions(a, b) {
    if (a.isOverdue !== b.isOverdue)
        return a.isOverdue ? -1 : 1;
    return a.waitingSince.localeCompare(b.waitingSince);
}
// --- Instrumentation (master plan S10: "log time-to-complete per action type") ---
exports.HM_TIMED_ACTIONS = [
    'request_hire',
    'approve',
    'reject',
    'send_back',
    'resubmit',
    'revise',
    'hold',
    'resume',
    'withdraw',
];
/** A single sitting longer than this is a tab left open, not a measurement. */
exports.HM_MAX_TIMED_MS = 60 * 60 * 1000;
exports.recordHmTimingSchema = zod_1.z.object({
    action: zod_1.z.enum(exports.HM_TIMED_ACTIONS),
    requisitionId: schemas_1.uuidSchema,
    /** From opening the action to the server accepting it, measured in the browser. */
    durationMs: zod_1.z.number().int().min(0).max(exports.HM_MAX_TIMED_MS),
});
//# sourceMappingURL=hiring-manager.js.map